import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Sparkles, RefreshCw, Trash2, Plus, Download, Copy, ChevronLeft, ChevronRight,
  CalendarClock, X as XIcon,
} from "lucide-react";
import { api, API, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { DEFAULT_TIMEZONE } from "../../lib/timezones";

/* Campaign builder: a goal in, a set of finished graphics out.
   Each slide is a listing photo with a headline composed onto it by the
   backend (Pillow), so what you see here is exactly the file you'll post. */

const SIZE_LABELS = { s: "S", m: "M", default: "Default", l: "L", xl: "XL" };
const POSITION_LABELS = {
  auto: "Auto", top: "Top", middle: "Middle", bottom: "Bottom", centered: "Centered",
};

/** Rendered slide. Fetched through the API client so auth headers apply, and
 *  re-fetched whenever `version` changes (after a style/headline/photo edit). */
function SlideImage({ campaignId, slideId, version, alt, style }) {
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const urlRef = useRef("");
  const reqRef = useRef(0);

  useEffect(() => {
    let alive = true;
    const myReq = ++reqRef.current;
    setFailed(false);
    setBusy(true);
    api.get(`/campaigns/${campaignId}/slides/${slideId}/render`, { responseType: "blob" })
      .then(({ data }) => {
        if (!alive || myReq !== reqRef.current) return;
        const next = URL.createObjectURL(data);
        const prev = urlRef.current;
        urlRef.current = next;
        setSrc(next);
        setBusy(false);
        // Only now is the old URL genuinely unused. Revoking it in the effect
        // cleanup killed the URL while <img src> still pointed at it, so every
        // keystroke flashed a broken-image icon.
        if (prev) URL.revokeObjectURL(prev);
      })
      .catch(() => { if (alive && myReq === reqRef.current) { setFailed(true); setBusy(false); } });
    return () => { alive = false; };
  }, [campaignId, slideId, version]);

  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  if (failed) {
    return (
      <div style={{ ...style, background: "var(--surface-alt)", display: "grid", placeItems: "center", color: "var(--text-muted)", fontSize: 13, padding: 16, textAlign: "center" }}>
        Couldn't render this slide. The photo may have been removed.
      </div>
    );
  }
  if (!src) {
    return (
      <div style={{ ...style, background: "var(--surface-alt)", display: "grid", placeItems: "center", color: "var(--text-muted)", fontSize: 12.5 }}>
        Rendering...
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt || ""}
      data-busy={busy ? "true" : "false"}
      style={{ ...style, opacity: busy ? 0.55 : 1, transition: "opacity 160ms ease" }}
    />
  );
}

function PhotoThumb({ photoId, style }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let alive = true;
    let objectUrl = "";
    api.get(`/listing-photos/${photoId}/file`, { responseType: "blob" })
      .then(({ data }) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(data);
        setSrc(objectUrl);
      })
      .catch(() => {});
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [photoId]);
  if (!src) return <div style={{ ...style, background: "var(--surface-alt)" }} />;
  return <img src={src} alt="" style={style} />;
}

/* Marks a field that can be left blank. Worth saying plainly: all three of the
   extra text lines arrive pre-filled, and an agent who clears one should know
   the slide is fine without it rather than wondering what broke. */
function Optional() {
  return <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>(optional)</span>;
}

function Segmented({ options, value, onChange, testid }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid={testid}>
      {options.map((o) => {
        const on = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            data-testid={`${testid}-${o.id}`}
            style={{
              border: `1px solid ${on ? "var(--primary)" : "var(--border)"}`,
              background: on ? "var(--primary)" : "transparent",
              color: on ? "#fff" : "var(--text-muted)",
              borderRadius: 8, padding: "7px 14px", fontSize: 13,
              fontWeight: on ? 600 : 400, cursor: "pointer", transition: "all 120ms ease",
            }}
          >{o.label}</button>
        );
      })}
    </div>
  );
}

function CaptionToggle({ on, onToggle, title, hint, testid }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "36px 1fr", gap: 10, alignItems: "start", padding: "6px 0" }}>
      <button
        type="button"
        data-testid={testid}
        onClick={() => onToggle(!on)}
        style={{
          border: "none", cursor: "pointer", flexShrink: 0, marginTop: 2,
          width: 34, height: 19, borderRadius: 999, padding: 2,
          background: on ? "var(--primary)" : "var(--border)",
          display: "inline-flex", alignItems: "center",
          justifyContent: on ? "flex-end" : "flex-start",
          transition: "all 120ms ease",
        }}
      >
        <span style={{ width: 15, height: 15, borderRadius: "50%", background: "#fff", display: "block" }} />
      </button>
      <div style={{ opacity: on ? 1 : 0.55 }}>
        <div style={{ fontSize: 13.5, fontWeight: 500 }}>{title}</div>
        {hint && <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 1, lineHeight: 1.45 }}>{hint}</div>}
      </div>
    </div>
  );
}

/** Pretty-print a "YYYY-MM-DDTHH:MM" wall-clock string without timezone math —
 *  the value is already in the agent's own zone, so re-interpreting it would
 *  shift it. Parsing the parts by hand keeps it literal. */
function formatSchedule(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value || "");
  if (!m) return "";
  const [, y, mo, d, hh, mm] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d));
  const day = date.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
  const h = Number(hh);
  const ampm = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${day} at ${h12}:${mm} ${ampm}`;
}

/**
 * Scheduling a campaign doesn't publish it — we can't post to Instagram on
 * someone's behalf without Meta app review. What it does is hand the agent the
 * finished graphics and caption by email at the moment she meant to post,
 * which is the part that actually gets forgotten.
 */
function ScheduleRow({ campaign, patch, timezone }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(campaign?.scheduled_at || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(campaign?.scheduled_at || "");
    setEditing(false);
  }, [campaign?.campaign_id, campaign?.scheduled_at]);

  const scheduled = campaign?.scheduled_at || "";
  const sent = campaign?.reminder_sent;

  const commit = async (value) => {
    setSaving(true);
    await patch({ scheduled_at: value });
    setSaving(false);
    setEditing(false);
  };

  const zoneLabel = (timezone || DEFAULT_TIMEZONE).split("/").pop().replace(/_/g, " ");

  return (
    <div
      data-testid="campaign-schedule"
      style={{
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        padding: "12px 20px", borderBottom: "1px solid var(--border)",
        background: scheduled && !sent ? "var(--surface-alt)" : "transparent",
      }}
    >
      <CalendarClock size={15} style={{ color: "var(--primary)", flexShrink: 0 }} />

      {editing || !scheduled ? (
        <>
          <input
            type="datetime-local"
            className="input-field"
            style={{ maxWidth: 240 }}
            value={draft}
            disabled={saving}
            data-testid="campaign-schedule-input"
            onChange={(e) => setDraft(e.target.value)}
          />
          <button
            type="button" className="btn-secondary" disabled={saving || !draft}
            onClick={() => commit(draft)} data-testid="campaign-schedule-save"
          >
            {saving ? "Saving…" : "Remind me then"}
          </button>
          <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
            We'll email you the images and caption at that moment ({zoneLabel} time).
          </span>
        </>
      ) : (
        <>
          <span style={{ fontSize: 14 }}>
            {sent ? "Reminder sent for " : "Reminder set for "}
            <b>{formatSchedule(scheduled)}</b>
          </span>
          <button
            type="button" className="btn-ghost" onClick={() => setEditing(true)}
            style={{ padding: 0, fontSize: 13, fontWeight: 600, color: "var(--primary)" }}
            data-testid="campaign-schedule-edit"
          >
            Change
          </button>
          <button
            type="button" className="btn-ghost" onClick={() => commit("")} disabled={saving}
            title="Clear the schedule" data-testid="campaign-schedule-clear"
            style={{ padding: 2, color: "var(--text-muted)" }}
          >
            <XIcon size={14} />
          </button>
        </>
      )}
    </div>
  );
}

export default function CampaignBuilder({ sellerId, photos, setToast }) {
  const { user } = useAuth();
  const timezone = user?.timezone || DEFAULT_TIMEZONE;
  const [options, setOptions] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [active, setActive] = useState(null);       // the campaign being edited
  const [slideIdx, setSlideIdx] = useState(0);
  const [version, setVersion] = useState(0);        // bumps to re-render previews
  const [building, setBuilding] = useState(false);
  const [regenning, setRegenning] = useState(false);
  const [form, setForm] = useState({ goal: "launch", fmt: "post", platform: "instagram", emphasis: "" });
  const [headlineDraft, setHeadlineDraft] = useState("");
  // The three optional lines. Label and detail belong to the slide you're
  // looking at; the footer is one line for the whole campaign.
  const [kickerDraft, setKickerDraft] = useState("");
  const [subheadDraft, setSubheadDraft] = useState("");
  const [footerDraft, setFooterDraft] = useState("");
  const [captionDraft, setCaptionDraft] = useState("");
  const [captionRegenning, setCaptionRegenning] = useState(false);
  const [assetMode, setAssetMode] = useState("post");   // "post" | "story"
  const headlineTimer = useRef(null);
  const kickerTimer = useRef(null);
  const subheadTimer = useRef(null);
  const footerTimer = useRef(null);
  const captionTimer = useRef(null);

  useEffect(() => {
    api.get("/admin/campaign-options").then(({ data }) => setOptions(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!sellerId) return;
    api.get(`/admin/seller/${sellerId}/campaigns`)
      .then(({ data }) => {
        setCampaigns(data.campaigns || []);
        if (!active && (data.campaigns || []).length) selectCampaign(data.campaigns[0]);
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, [sellerId]);

  const selectCampaign = (c) => {
    setActive(c);
    setSlideIdx(0);
    setAssetMode("post");
    setCaptionDraft(c.caption_body ?? c.caption ?? "");
    setHeadlineDraft(c.slides?.[0]?.headline || "");
    setKickerDraft(c.slides?.[0]?.kicker || "");
    setSubheadDraft(c.slides?.[0]?.subhead || "");
    setFooterDraft(c.footer || "");
    setVersion((v) => v + 1);
  };

  const slides = active?.slides || [];
  const storySlide = active?.story_slide || null;
  const showingStory = assetMode === "story" && !!storySlide;
  // Everything below edits whichever asset is selected.
  const slide = showingStory ? storySlide : slides[slideIdx];
  const slideFmt = showingStory ? "story" : (active?.fmt || "post");

  useEffect(() => {
    setHeadlineDraft(slide?.headline || "");
    setKickerDraft(slide?.kicker || "");
    setSubheadDraft(slide?.subhead || "");
  }, [slide?.slide_id, assetMode]); // eslint-disable-line

  const applyCampaign = useCallback((c) => {
    setActive(c);
    setCampaigns((xs) => xs.map((x) => (x.campaign_id === c.campaign_id ? c : x)));
    setVersion((v) => v + 1);
  }, []);

  const patch = useCallback(async (body) => {
    if (!active) return;
    try {
      const { data } = await api.put(`/admin/campaigns/${active.campaign_id}`, body);
      applyCampaign(data.campaign);
      return data.campaign;
    } catch (err) {
      setToast(formatApiError(err?.response?.data?.detail) || "Couldn't save that change");
    }
  }, [active, applyCampaign, setToast]);

  const build = async () => {
    if (!photos.length) {
      setToast("Upload some listing photos first — the campaign is built from them.");
      return;
    }
    setBuilding(true);
    try {
      const { data } = await api.post(
        `/admin/seller/${sellerId}/campaigns/generate`, form, { timeout: 180000 },
      );
      setCampaigns((xs) => [data.campaign, ...xs]);
      selectCampaign(data.campaign);
      setToast("Campaign built. Edit any slide, then download when you're happy.");
    } catch (err) {
      setToast(formatApiError(err?.response?.data?.detail) || "Couldn't build the campaign");
    } finally { setBuilding(false); }
  };

  const regenerateHeadline = async () => {
    if (!slide) return;
    setRegenning(true);
    try {
      const { data } = await api.post(
        `/admin/campaigns/${active.campaign_id}/slides/${slide.slide_id}/regenerate`, {}, { timeout: 120000 },
      );
      applyCampaign(data.campaign);
      const fresh = data.campaign.slides.find((s) => s.slide_id === slide.slide_id);
      setHeadlineDraft(fresh?.headline || "");
    } catch (err) {
      setToast(formatApiError(err?.response?.data?.detail) || "Couldn't rewrite that headline");
    } finally { setRegenning(false); }
  };

  /* Headline, label and detail all save the same way: the draft updates on
     every keystroke so typing stays instant, and the change is written through
     after a pause so we aren't PUTting the campaign on every letter. */
  const saveSlideField = (field, val, timer) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (showingStory) patch({ story_slide: { ...storySlide, [field]: val } });
      else patch({ slides: slides.map((s, i) => (i === slideIdx ? { ...s, [field]: val } : s)) });
    }, 700);
  };

  const onHeadlineChange = (val) => { setHeadlineDraft(val); saveSlideField("headline", val, headlineTimer); };
  const onKickerChange = (val) => { setKickerDraft(val); saveSlideField("kicker", val, kickerTimer); };
  const onSubheadChange = (val) => { setSubheadDraft(val); saveSlideField("subhead", val, subheadTimer); };

  // The footer belongs to the campaign, not the slide — one edit changes every
  // slide and the Story at once.
  const onFooterChange = (val) => {
    setFooterDraft(val);
    clearTimeout(footerTimer.current);
    footerTimer.current = setTimeout(() => patch({ footer: val }), 700);
  };

  const onCaptionChange = (val) => {
    setCaptionDraft(val);
    clearTimeout(captionTimer.current);
    captionTimer.current = setTimeout(() => patch({ caption_body: val }), 700);
  };

  const setPhoto = (photoId) =>
    showingStory
      ? patch({ story_slide: { ...storySlide, photo_id: photoId } })
      : patch({ slides: slides.map((s, i) => (i === slideIdx ? { ...s, photo_id: photoId } : s)) });

  const setStyle = (key, val) => patch({ style: { ...(active.style || {}), [key]: val } });

  const addSlide = async () => {
    if (slides.length >= 10) { setToast("Ten slides is the maximum."); return; }
    const next = [...slides];
    next.splice(slideIdx + 1, 0, {
      slide_id: `new_${Date.now()}`,
      photo_id: photos[(slideIdx + 1) % photos.length]?.photo_id,
      headline: "NEW SLIDE",
      role: "feature",
      kicker: "",
      subhead: "",
    });
    const c = await patch({ slides: next });
    if (c) setSlideIdx(Math.min(slideIdx + 1, c.slides.length - 1));
  };

  const removeSlide = async () => {
    if (slides.length <= 1) { setToast("A campaign needs at least one slide."); return; }
    const c = await patch({ slides: slides.filter((_, i) => i !== slideIdx) });
    if (c) setSlideIdx(Math.max(0, Math.min(slideIdx, c.slides.length - 1)));
  };

  const deleteCampaign = async () => {
    if (!window.confirm("Delete this campaign? The photos stay in your library.")) return;
    await api.delete(`/admin/campaigns/${active.campaign_id}`);
    const rest = campaigns.filter((c) => c.campaign_id !== active.campaign_id);
    setCampaigns(rest);
    if (rest.length) selectCampaign(rest[0]); else setActive(null);
  };

  const copyCaption = async () => {
    // `active.caption` is the assembled version (body + link + contact + tags).
    try {
      await navigator.clipboard.writeText(active.caption || captionDraft);
      setToast("Caption copied — everything below the box included.");
    } catch { setToast("Couldn't copy automatically — select the text and copy it manually."); }
  };

  const regenerateCaption = async () => {
    setCaptionRegenning(true);
    try {
      const { data } = await api.post(
        `/admin/campaigns/${active.campaign_id}/caption/regenerate`, {}, { timeout: 120000 },
      );
      applyCampaign(data.campaign);
      setCaptionDraft(data.campaign.caption_body || "");
      setToast("Caption rewritten.");
    } catch (err) {
      setToast(formatApiError(err?.response?.data?.detail) || "Couldn't rewrite the caption");
    } finally { setCaptionRegenning(false); }
  };

  const setCaptionSetting = (key, val) =>
    patch({ caption_settings: { ...(active.caption_settings || {}), [key]: val } });

  const downloadUrl = active ? `${API}/admin/campaigns/${active.campaign_id}/download` : "#";

  const goals = options?.goals || [];
  const fonts = options?.fonts || [];
  const sizes = options?.sizes || [];
  const positions = options?.positions || [];
  const isStory = ["story", "reel", "video"].includes(active?.fmt);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* ---------- Setup ---------- */}
      <div className="card" style={{ padding: 24, maxWidth: 860 }}>
        <div className="overline">Build a campaign</div>
        <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4, marginBottom: 16, maxWidth: 600 }}>
          Pick what this campaign is for and we'll write the headlines, lay them over your photos, and
          write one caption for the whole set. You download the finished images and post them yourself.
        </p>

        <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 8 }}>Goal</label>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 10 }}>
          {goals.map((g) => {
            const on = form.goal === g.id;
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => setForm({ ...form, goal: g.id })}
                data-testid={`campaign-goal-${g.id}`}
                style={{
                  textAlign: "left", cursor: "pointer", padding: "12px 14px", borderRadius: 12,
                  border: `1px solid ${on ? "var(--primary)" : "var(--border)"}`,
                  background: on ? "var(--surface-alt)" : "transparent",
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 14, color: on ? "var(--primary)" : "var(--text)" }}>{g.label}</div>
                <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 }}>{g.blurb}</div>
              </button>
            );
          })}
        </div>

        <div className="form-grid" style={{ marginTop: 16 }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Platform</label>
            <select className="input-field" data-testid="campaign-platform" value={form.platform}
                    onChange={(e) => setForm({ ...form, platform: e.target.value })}>
              {(options?.platforms || []).map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Format</label>
            <select className="input-field" data-testid="campaign-format" value={form.fmt}
                    onChange={(e) => setForm({ ...form, fmt: e.target.value })}>
              <option value="post">Feed post / carousel</option>
              <option value="story">Story (9:16)</option>
              <option value="reel">Reel cover (9:16)</option>
            </select>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>
              Anything to emphasize? <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>(optional)</span>
            </label>
            <input className="input-field" data-testid="campaign-emphasis"
                   placeholder="e.g. lead with the backyard; open house Saturday 11–2"
                   value={form.emphasis} onChange={(e) => setForm({ ...form, emphasis: e.target.value })} />
          </div>
        </div>

        <button type="button" className="btn-primary" onClick={build} disabled={building || !photos.length}
                style={{ marginTop: 16 }} data-testid="campaign-build-btn">
          <Sparkles size={14} /> {building ? "Building…" : "Build campaign"}
        </button>
        {!photos.length && (
          <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 8 }}>
            Upload listing photos above first — campaigns are built from them.
          </div>
        )}
      </div>

      {/* ---------- Editor ---------- */}
      {active && slide && (
        <div className="card" style={{ padding: 0, maxWidth: 1040, overflow: "hidden" }} data-testid="campaign-editor">
          <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", background: "var(--surface-alt)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span className="badge">{goals.find((g) => g.id === active.goal)?.label || active.goal}</span>
              {storySlide ? (
                <div style={{ display: "inline-flex", gap: 3, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 999, padding: 3 }} data-testid="campaign-asset-tabs">
                  {[
                    { id: "post", label: `Post · ${slides.length} photo${slides.length === 1 ? "" : "s"}` },
                    { id: "story", label: "Story · 1 photo" },
                  ].map((t) => {
                    const on = assetMode === t.id;
                    return (
                      <button key={t.id} type="button" onClick={() => setAssetMode(t.id)}
                              data-testid={`campaign-asset-${t.id}`}
                              style={{
                                border: "none", cursor: on ? "default" : "pointer", borderRadius: 999,
                                padding: "5px 14px", fontSize: 12, fontWeight: on ? 600 : 400,
                                background: on ? "var(--primary)" : "transparent",
                                color: on ? "#fff" : "var(--text-muted)", transition: "all 120ms ease",
                              }}>{t.label}</button>
                    );
                  })}
                </div>
              ) : (
                <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                  {slides.length} slide{slides.length === 1 ? "" : "s"} · 1080×1920
                </span>
              )}
              <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                {showingStory ? "1080×1920" : (active.fmt === "post" ? "1080×1080" : "1080×1920")}
              </span>
              {campaigns.length > 1 && (
                <select className="input-field" style={{ width: "auto", padding: "5px 8px", fontSize: 12.5 }}
                        value={active.campaign_id}
                        onChange={(e) => selectCampaign(campaigns.find((c) => c.campaign_id === e.target.value))}
                        data-testid="campaign-switcher">
                  {campaigns.map((c) => (
                    <option key={c.campaign_id} value={c.campaign_id}>
                      {goals.find((g) => g.id === c.goal)?.label || c.goal} · {(c.created_at || "").slice(0, 10)}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              <a className="btn-secondary" href={downloadUrl} data-testid="campaign-download"
                 style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Download size={14} /> Download all
              </a>
              <button type="button" className="btn-ghost" onClick={deleteCampaign} title="Delete campaign" data-testid="campaign-delete">
                <Trash2 size={14} />
              </button>
            </div>
          </div>

          <ScheduleRow campaign={active} patch={patch} timezone={timezone} />

          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 360px", gap: 0 }} className="campaign-grid">
            {/* left: controls */}
            <div style={{ padding: 20, borderRight: "1px solid var(--border)" }}>
              {showingStory && (
                <div style={{ background: "var(--surface-alt)", borderRadius: 10, padding: "10px 12px", fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 }}>
                  The Story is a single full-screen image built from this campaign. It shares the caption with the post.
                </div>
              )}
              {!showingStory && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, background: "var(--surface-alt)", borderRadius: 10, padding: "8px 10px" }}>
                <button type="button" className="btn-ghost" disabled={slideIdx === 0}
                        onClick={() => setSlideIdx((i) => Math.max(0, i - 1))} data-testid="campaign-prev">
                  <ChevronLeft size={16} />
                </button>
                <span style={{ fontSize: 12.5, fontWeight: 600, letterSpacing: "0.04em", color: "var(--primary)" }}>
                  SLIDE {slideIdx + 1} / {slides.length}{slide.role ? ` · ${slide.role}` : ""}
                </span>
                <button type="button" className="btn-ghost" disabled={slideIdx >= slides.length - 1}
                        onClick={() => setSlideIdx((i) => Math.min(slides.length - 1, i + 1))} data-testid="campaign-next">
                  <ChevronRight size={16} />
                </button>
              </div>
              )}

              {!isStory && !showingStory && (
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <button type="button" className="btn-secondary" onClick={addSlide} style={{ flex: 1 }} data-testid="campaign-add-slide">
                    <Plus size={14} /> Add slide
                  </button>
                  <button type="button" className="btn-ghost" onClick={removeSlide} style={{ flex: 1, border: "1px solid var(--border)", borderRadius: 999 }} data-testid="campaign-remove-slide">
                    <Trash2 size={14} /> Remove slide
                  </button>
                </div>
              )}

              {/* headline */}
              <div style={{ marginTop: 18 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <label style={{ fontSize: 13, fontWeight: 500 }}>Headline</label>
                  <button type="button" className="btn-ghost" onClick={regenerateHeadline} disabled={regenning} data-testid="campaign-regen-headline">
                    <RefreshCw size={13} /> {regenning ? "Rewriting…" : "Regenerate"}
                  </button>
                </div>
                <input className="input-field" data-testid="campaign-headline" value={headlineDraft}
                       onChange={(e) => onHeadlineChange(e.target.value)} placeholder="BACKYARD VIEWS" />
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 5 }}>
                  Short works best — two to four words sit cleanly over a photo.
                </div>

                <label style={{ fontSize: 13, fontWeight: 500, display: "block", margin: "14px 0 6px" }}>
                  Label above <Optional />
                </label>
                <input className="input-field" data-testid="campaign-kicker" value={kickerDraft} maxLength={40}
                       onChange={(e) => onKickerChange(e.target.value)} placeholder="Just Listed" />
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 5 }}>
                  Small caps over the headline — which lets the headline itself stay short.
                </div>

                <label style={{ fontSize: 13, fontWeight: 500, display: "block", margin: "14px 0 6px" }}>
                  Detail line <Optional />
                </label>
                <input className="input-field" data-testid="campaign-subhead" value={subheadDraft} maxLength={120}
                       onChange={(e) => onSubheadChange(e.target.value)}
                       placeholder="123 Maple St · $675,000 · 4 bd / 3 ba" />
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 5 }}>
                  Sits under the headline. Two lines at most — a longer one gets trimmed rather than
                  pushed off the photo.
                </div>
              </div>

              {/* text style */}
              <div style={{ marginTop: 18, borderTop: "1px solid var(--border)", paddingTop: 16 }}>
                <div className="overline" style={{ marginBottom: 10 }}>Text style</div>

                <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Font</label>
                <select className="input-field" data-testid="campaign-font" value={active.style?.font || "outfit"}
                        onChange={(e) => setStyle("font", e.target.value)}>
                  {fonts.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
                <div style={{ fontSize: 12, color: "var(--text-muted)", margin: "5px 0 12px" }}>Applies to every slide in this campaign.</div>

                <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Headline size</label>
                <Segmented testid="campaign-size" value={active.style?.size || "default"}
                           options={sizes.map((s) => ({ id: s, label: SIZE_LABELS[s] || s }))}
                           onChange={(v) => setStyle("size", v)} />

                <label style={{ fontSize: 13, fontWeight: 500, display: "block", margin: "14px 0 6px" }}>Text position</label>
                <Segmented testid="campaign-position" value={active.style?.position || "auto"}
                           options={positions.map((p) => ({ id: p, label: POSITION_LABELS[p] || p }))}
                           onChange={(v) => setStyle("position", v)} />
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 5 }}>
                  Auto reads each photo and puts the text where it stays readable.
                </div>

                <label style={{ fontSize: 13, fontWeight: 500, display: "block", margin: "14px 0 6px" }}>
                  Footer <Optional />
                </label>
                <input className="input-field" data-testid="campaign-footer" value={footerDraft} maxLength={140}
                       onChange={(e) => onFooterChange(e.target.value)}
                       placeholder="Your name · Brokerage · Lic #" />
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 5 }}>
                  Pinned along the bottom of every slide. Filled in from Content → Branding — most
                  states require brokerage attribution on listing advertising.
                </div>
              </div>

              {/* photo for this slide */}
              <div style={{ marginTop: 18, borderTop: "1px solid var(--border)", paddingTop: 16 }}>
                <div className="overline">Photo for this slide</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", margin: "4px 0 10px" }}>Click a photo to use it on this slide only.</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(84px, 1fr))", gap: 8 }}>
                  {photos.map((ph) => {
                    const on = ph.photo_id === slide.photo_id;
                    return (
                      <button key={ph.photo_id} type="button" onClick={() => setPhoto(ph.photo_id)}
                              data-testid={`campaign-photo-${ph.photo_id}`}
                              style={{
                                position: "relative", padding: 0, cursor: "pointer", background: "none",
                                border: on ? "2px solid var(--primary)" : "1px solid var(--border)",
                                borderRadius: 8, overflow: "hidden",
                              }}>
                        <PhotoThumb photoId={ph.photo_id} style={{ width: "100%", height: 62, objectFit: "cover", display: "block" }} />
                        {on && (
                          <span style={{ position: "absolute", top: 4, left: 4, background: "var(--primary)", color: "#fff", fontSize: 9, fontWeight: 600, padding: "2px 6px", borderRadius: 999, letterSpacing: "0.04em" }}>
                            ON SLIDE
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* caption */}
              <div style={{ marginTop: 18, borderTop: "1px solid var(--border)", paddingTop: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div className="overline">Caption</div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button type="button" className="btn-ghost" onClick={regenerateCaption}
                            disabled={captionRegenning} data-testid="campaign-regen-caption">
                      <RefreshCw size={13} /> {captionRegenning ? "Rewriting…" : "Regenerate"}
                    </button>
                    <button type="button" className="btn-ghost" onClick={copyCaption} data-testid="campaign-copy-caption">
                      <Copy size={13} /> Copy
                    </button>
                  </div>
                </div>

                <textarea className="input-field" data-testid="campaign-caption" rows={10}
                          value={captionDraft} onChange={(e) => onCaptionChange(e.target.value)}
                          style={{ resize: "vertical", lineHeight: 1.6 }} />
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 5 }}>
                  Type over any of it — this is your caption, the AI just gets it started.
                </div>

                {/* what gets stitched on underneath */}
                <div style={{ marginTop: 14, background: "var(--surface-alt)", borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 10 }}>
                    Added underneath
                  </div>

                  <CaptionToggle
                    testid="caption-contact"
                    on={active.caption_settings?.include_contact !== false}
                    onToggle={(v) => setCaptionSetting("include_contact", v)}
                    title="Your name and contact"
                    hint={[active.contact?.name, active.contact?.email].filter(Boolean).join(" · ") || "Set on your profile"}
                  />

                  <CaptionToggle
                    testid="caption-hashtags"
                    on={active.caption_settings?.include_hashtags !== false}
                    onToggle={(v) => setCaptionSetting("include_hashtags", v)}
                    title="Hashtags"
                    hint={`${(active.hashtags || []).length} tags`}
                  />

                  <CaptionToggle
                    testid="caption-link"
                    on={!!active.caption_settings?.include_link}
                    onToggle={(v) => setCaptionSetting("include_link", v)}
                    title="A link"
                    hint="Facebook only — Instagram doesn't make links in captions clickable."
                  />
                  {active.caption_settings?.include_link && (
                    <input
                      className="input-field"
                      data-testid="caption-link-url"
                      style={{ marginTop: 8, fontSize: 13 }}
                      placeholder="https://…"
                      defaultValue={active.caption_settings?.link_url || ""}
                      onBlur={(e) => setCaptionSetting("link_url", e.target.value)}
                    />
                  )}

                  {(active.hashtags || []).length > 0 && active.caption_settings?.include_hashtags !== false && (
                    <div style={{ marginTop: 10, color: "var(--primary)", fontSize: 12.5, lineHeight: 1.7, wordBreak: "break-word" }}>
                      {(active.hashtags || []).map((h) => `#${h}`).join(" ")}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* right: live preview */}
            <div className="campaign-preview" style={{ padding: 20, background: "var(--surface-alt)" }}>
              <div className="overline" style={{ marginBottom: 10 }}>Preview</div>
              <SlideImage
                campaignId={active.campaign_id}
                slideId={slide.slide_id}
                version={version}
                alt={slide.headline}
                style={{
                  width: showingStory ? "72%" : "100%",
                  margin: showingStory ? "0 auto" : undefined,
                  aspectRatio: slideFmt === "post" || slideFmt === "carousel" ? "1 / 1" : "9 / 16",
                  objectFit: "cover", borderRadius: 12, display: "block",
                  border: "1px solid var(--border)",
                }}
              />
              {!showingStory && (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 10 }}>
                <button type="button" className="btn-ghost" disabled={slideIdx === 0}
                        onClick={() => setSlideIdx((i) => Math.max(0, i - 1))}>
                  <ChevronLeft size={15} />
                </button>
                <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{slideIdx + 1} / {slides.length}</span>
                <button type="button" className="btn-ghost" disabled={slideIdx >= slides.length - 1}
                        onClick={() => setSlideIdx((i) => Math.min(slides.length - 1, i + 1))}>
                  <ChevronRight size={15} />
                </button>
              </div>
              )}
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 12, lineHeight: 1.5 }}>
                This is the actual image you'll download — text and all.
                {!showingStory && slides.length > 1 && " Download all gives you every picture in order, plus the caption."}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
